import type {
  MouseEvent as ReactMouseEvent,
  TouchEvent as ReactTouchEvent,
} from 'react';

import { useTooltip } from '../../core/components/overlay-tooltip/contexts/TooltipContext.js';
import type { Data } from '../../core/components/overlay-tooltip/types.js';
import { useTooltipActions } from '../../core/components/overlay-tooltip/useTooltipActions.js';
import { useEventErrorHandler } from '../../core/hooks/useEventErrorHandler.js';
import type { Formatter } from '../../core/types/formatter.js';
import type { InternalSliceData } from '../types/internal/pie-chart.js';
import { getTooltipContent } from '../utils/get-tooltip-content.js';

const toTooltipSeries = (
  sliceId: string,
  data: InternalSliceData,
  handlerCatch: (callback: unknown) => number,
  formatter: Formatter,
) => {
  const tooltipContent = getTooltipContent(
    sliceId,
    data,
    handlerCatch,
    formatter,
  );

  return {
    name: tooltipContent.name,
    value: tooltipContent.absoluteValue,
    color: tooltipContent.color,
    relativeValue: tooltipContent.relativeValue,
    relativeValueUnit: '%',
  };
};

const buildTooltipData = (
  sliceId: string,
  data: InternalSliceData,
  handlerCatch: (callback: unknown) => number,
  formatter: Formatter,
): Data => {
  const series = data.groupedSlices?.length
    ? data.groupedSlices.map((groupedSlice) =>
        toTooltipSeries(
          `slice-${groupedSlice.id}`,
          groupedSlice,
          handlerCatch,
          formatter,
        ),
      )
    : [toTooltipSeries(sliceId, data, handlerCatch, formatter)];

  return {
    metadata: {
      sliceId: data.id,
    },
    geometry: 'pie',
    series,
  };
};

/**
 * Hook that generates handlers for mouse and touch interactions.
 * @param sliceId - Id of the slice.
 * @param data - Slice data for this slice.
 * @param formatter - Formatter to be used.
 * @returns Handlers to perform mouse and touch interactions.
 */
export function useSliceHandlers(
  sliceId: string,
  data: InternalSliceData,
  formatter: Formatter,
) {
  const handlerCatch = useEventErrorHandler();
  const tooltipState = useTooltip();
  const { hover, pin, unpin } = useTooltipActions();

  const onPointerMoveHandler = (event: ReactMouseEvent) => {
    if (tooltipState.getState().pinned) {
      return;
    }

    const tooltipData = buildTooltipData(
      sliceId,
      data,
      handlerCatch,
      formatter,
    );

    hover(event, tooltipData);
  };

  const onMouseClickHandler = (event: ReactMouseEvent) => {
    event.stopPropagation();

    const tooltipData = buildTooltipData(
      sliceId,
      data,
      handlerCatch,
      formatter,
    );

    if (tooltipState.getState().pinned) {
      unpin();
      hover(event, tooltipData);
    } else {
      pin(event, tooltipData);
    }
  };

  // Handles tooltip pin/unpin on touch. Fires before the browser's synthetic click
  // so the pinned state is established before iOS can suppress that click event.
  // Calling preventDefault() suppresses the subsequent synthetic click so onClick
  // does not double-fire.
  const onTouchEndHandler = (event: ReactTouchEvent) => {
    if (event.cancelable) {
      event.preventDefault();
    }

    const touch = event.changedTouches[0];
    if (!touch) {
      return;
    }

    const tooltipData = buildTooltipData(
      sliceId,
      data,
      handlerCatch,
      formatter,
    );

    if (tooltipState.getState().pinned) {
      unpin();
    } else {
      pin({ clientX: touch.clientX, clientY: touch.clientY }, tooltipData);
    }
  };

  return {
    onPointerMoveHandler,
    onMouseClickHandler,
    onTouchEndHandler,
  };
}
